// Settings page with modern tabs
// This demonstrates React state management and conditional rendering

import React, { useState } from 'react';
import { User, Building, Key, Users, Sparkles, Copy, Trash2 } from 'lucide-react';
import { useStytchAuth } from '../hooks/useStytchAuth';
import TeamManagement from '../components/TeamManagement';

console.log('🔄 SETTINGS: Updated to use backend API for organization data');

const ADMIN_ROLE_IDS = new Set(['stytch_admin', 'admin']);

const normalizeRoleValues = (rawRoles) => {
  if (!rawRoles) return [];

  if (Array.isArray(rawRoles)) {
    return rawRoles
      .map((role) => {
        if (typeof role === 'string') return role.trim().toLowerCase();
        if (role && typeof role === 'object') return String(role.role_id || role.role || '').trim().toLowerCase();
        return '';
      })
      .filter(Boolean);
  }

  if (typeof rawRoles === 'string') {
    return rawRoles
      .split(',')
      .map((role) => role.trim().toLowerCase())
      .filter(Boolean);
  }

  return [];
};

const hasAdminRole = (rawRoles) => normalizeRoleValues(rawRoles).some((role) => ADMIN_ROLE_IDS.has(role));

const Settings = () => {
  // State to track which tab is active - like a class field in Java
  const [activeTab, setActiveTab] = useState('profile');
  const [adminService, setAdminService] = useState(null);
  const [organizationData, setOrganizationData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  const { stytch, session, member } = useStytchAuth();

  // Initialize authenticated admin service and load data
  React.useEffect(() => {
    const initAdminService = async () => {
      if (!stytch || !session) return;
      
      try {
        const jwt = stytch?.session.getTokens()?.session_jwt;
        if (!jwt) return;

        const service = (await import('../services/AdminService')).default;
        service.setAuthToken(jwt);
        setAdminService(service);
        console.log('✅ AdminService initialized with JWT token');
        
        // Load organization data
        await loadOrganizationData(service);
      } catch (error) {
        console.error('❌ Failed to initialize AdminService:', error);
        setDataError('Failed to initialize admin service');
        setDataLoading(false);
      }
    };

    initAdminService();
  }, [stytch, session]);

  // Load organization data from backend API
  const loadOrganizationData = async (service = adminService) => {
    if (!service) {
      setDataError('Admin service not available');
      setDataLoading(false);
      return;
    }

    try {
      setDataLoading(true);
      setDataError(null);

      const data = await service.getOrganization();
      setOrganizationData(data);
      console.log('✅ Organization data loaded from backend API');

    } catch (error) {
      console.error('❌ Failed to load organization data:', error);
      setDataError(error.message);
    } finally {
      setDataLoading(false);
    }
  };

  // Check if user has admin role
  const isAdmin = React.useMemo(() => {
    return hasAdminRole(organizationData?.profile?.role) || hasAdminRole(member?.roles);
  }, [organizationData, member]);

  // Tab configuration - like a constant array in Java
  // Organization, Team, and API Keys tabs are only visible to admins
  const allTabs = React.useMemo(() => [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'organization', label: 'Organization', icon: Building, adminOnly: true },
    { id: 'team', label: 'Team', icon: Users, adminOnly: true },
    { id: 'apiKeys', label: 'API Keys', icon: Key, adminOnly: true },
    { id: 'providerKeys', label: 'LLM Providers', icon: Sparkles, adminOnly: true }
  ], []);

  // Handle deep links (notification emails, and in-app links like
  // Simulation Studio's "Add one in Settings" pointing at ?tab=providerKeys).
  // Runs after isAdmin resolves so an admin-only tab is never activated for a
  // non-admin who lands on one of these links directly.
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    const action = urlParams.get('action');

    if (tab === 'team' && action === 'pending') {
      console.log('🔍 SETTINGS: Deep link detected - navigating to Team tab');
      setActiveTab('team');
      return;
    }
    const target = allTabs.find(t => t.id === tab);
    if (target && (!target.adminOnly || isAdmin)) {
      setActiveTab(tab);
    }
  }, [allTabs, isAdmin]);

  const tabs = React.useMemo(() => {
    // Filter out admin-only tabs if user is not admin
    return allTabs.filter(tab => !tab.adminOnly || isAdmin);
  }, [allTabs, isAdmin]);

  // Function to render tab content based on activeTab
  const renderTabContent = () => {
    // Show loading state if data is still loading
    if (dataLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-2 border-accent-primary-base border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-text-secondary">Loading organization data...</span>
        </div>
      );
    }

    // Show error state if data loading failed
    if (dataError) {
      return (
        <div className="bg-red-500/10 border border-red-500/30 rounded p-4">
          <p className="text-red-400">{dataError}</p>
          <button 
            onClick={() => loadOrganizationData()}
            className="mt-2 text-red-400 underline hover:text-red-300"
          >
            Try again
          </button>
        </div>
      );
    }

    switch (activeTab) {
      case 'profile':
        return <ProfileContent adminService={adminService} organizationData={organizationData} onDataChanged={() => loadOrganizationData()} />;
      case 'organization':
        return <OrganizationContent adminService={adminService} organizationData={organizationData} onDataChanged={() => loadOrganizationData()} />;
      case 'team':
        return <TeamManagement adminService={adminService} />;
      case 'apiKeys':
        return <ApiKeysContent adminService={adminService} />;
      case 'providerKeys':
        return <ProviderKeysContent />;
      default:
        return <ProfileContent adminService={adminService} organizationData={organizationData} onDataChanged={() => loadOrganizationData()} />;
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Page Header */}
      <div className="border-b border-accent-lavender/20 pb-4 mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Settings</h1>
        <p className="text-text-secondary mt-1">Manage your account and preferences</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-accent-lavender/20">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center space-x-2 pb-3 border-b-2 transition-colors
                  ${isActive
                    ? 'border-accent-primary-base text-text-primary'
                    : 'border-transparent text-text-muted hover:text-text-secondary'
                  }
                `}
              >
                <Icon size={18} />
                <span className="font-medium">{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="flex-1 pt-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

// Profile Tab Content Component
const ProfileContent = ({ adminService, organizationData, onDataChanged }) => {
  const [profileData, setProfileData] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState(null);

  React.useEffect(() => {
    if (organizationData) {
      loadProfileData();
    }
  }, [organizationData]);

  const loadProfileData = () => {
    if (!organizationData) return;

    try {
      const profile = organizationData.profile || {};
      
      // Populate form with saved data from backend API
      setProfileData({
        firstName: profile.first_name || '',
        lastName: profile.last_name || '',
        email: profile.email || '',
        phone: profile.phone || ''
      });

    } catch (err) {
      console.error('Failed to load profile data:', err);
    }
  };

  const saveProfileData = async () => {
    if (!adminService) {
      setSaveError('Admin service not available');
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);

      await adminService.updateProfile(profileData);
      console.log('Profile saved successfully');

      // Reload organization data from backend to refresh state
      if (onDataChanged) {
        onDataChanged().catch(err => {
          console.warn('Failed to reload data after save:', err);
          // Don't fail the save operation if reload fails
        });
      }

    } catch (err) {
      console.error('Failed to save profile:', err);
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      {saveError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded p-3 mb-4">
          <p className="text-red-400 text-sm">{saveError}</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* First Name */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            First Name
          </label>
          <input
            type="text"
            value={profileData.firstName}
            onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
            className="w-full px-3 py-2 bg-background-secondary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
            placeholder="Enter first name"
          />
        </div>

        {/* Last Name */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Last Name
          </label>
          <input
            type="text"
            value={profileData.lastName}
            onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
            className="w-full px-3 py-2 bg-background-secondary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
            placeholder="Enter last name"
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Email Address
        </label>
        <input
          type="email"
          value={profileData.email}
          className="w-full px-3 py-2 bg-background-secondary border border-accent-lavender/20 rounded text-text-secondary cursor-not-allowed"
          disabled={true}
          readOnly={true}
        />
      </div>

      {/* Phone */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Phone Number
        </label>
        <input
          type="tel"
          value={profileData.phone}
          onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
          className="w-full px-3 py-2 bg-background-secondary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
          placeholder="Enter phone number"
        />
      </div>

      {/* Save Button */}
      <div className="pt-4">
        <button 
          onClick={saveProfileData}
          disabled={saving}
          className="bg-accent-primary-base text-text-inverse px-3 py-1 rounded-full text-xs font-medium hover:bg-accent-primary-vibrant transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

// Organization Tab Content Component
const OrganizationContent = ({ adminService, organizationData, onDataChanged }) => {
  const [orgData, setOrgData] = React.useState({
    organizationName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States'
  });
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState(null);

  React.useEffect(() => {
    if (organizationData) {
      loadOrganizationData();
    }
  }, [organizationData]);

  const loadOrganizationData = () => {
    if (!organizationData) return;

    try {
      const organization = organizationData.organization || {};
      const address = organizationData.address || {};
      
      // Populate form with saved data from backend API
      setOrgData({
        organizationName: organization.organization_name || '',
        addressLine1: address.street || '',
        addressLine2: address.street2 || '',
        city: address.city || '',
        state: address.state || '',
        zipCode: address.zip_code || '',
        country: address.country || 'United States'
      });

    } catch (err) {
      console.error('Failed to load organization data:', err);
    }
  };

  const saveOrganizationData = async () => {
    if (!adminService) {
      setSaveError('Admin service not available');
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);

      await adminService.updateOrganizationDetails(orgData);
      console.log('Organization saved successfully');

      // Reload organization data from backend to refresh state
      if (onDataChanged) {
        await onDataChanged();
      }

    } catch (err) {
      console.error('Failed to save organization:', err);
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      {saveError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded p-3 mb-4">
          <p className="text-red-400 text-sm">{saveError}</p>
        </div>
      )}

      {/* Organization Name */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Organization Name
        </label>
        <input
          type="text"
          value={orgData.organizationName}
          onChange={(e) => setOrgData(prev => ({ ...prev, organizationName: e.target.value }))}
          className="w-full px-3 py-2 bg-background-secondary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
          placeholder="Enter organization name"
        />
      </div>

      {/* Address Fields */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-text-primary">Address</h3>
        
        {/* Address Line 1 */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Address Line 1
          </label>
          <input
            type="text"
            value={orgData.addressLine1}
            onChange={(e) => setOrgData(prev => ({ ...prev, addressLine1: e.target.value }))}
            className="w-full px-3 py-2 bg-background-secondary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
            placeholder="Street address"
          />
        </div>

        {/* Address Line 2 */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Address Line 2 (Optional)
          </label>
          <input
            type="text"
            value={orgData.addressLine2}
            onChange={(e) => setOrgData(prev => ({ ...prev, addressLine2: e.target.value }))}
            className="w-full px-3 py-2 bg-background-secondary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
            placeholder="Apartment, suite, etc."
          />
        </div>

        {/* City, State, ZIP */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              City
            </label>
            <input
              type="text"
              value={orgData.city}
              onChange={(e) => setOrgData(prev => ({ ...prev, city: e.target.value }))}
              className="w-full px-3 py-2 bg-background-secondary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
              placeholder="City"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              State
            </label>
            <input
              type="text"
              value={orgData.state}
              onChange={(e) => setOrgData(prev => ({ ...prev, state: e.target.value }))}
              className="w-full px-3 py-2 bg-background-secondary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
              placeholder="State"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              ZIP Code
            </label>
            <input
              type="text"
              value={orgData.zipCode}
              onChange={(e) => setOrgData(prev => ({ ...prev, zipCode: e.target.value }))}
              className="w-full px-3 py-2 bg-background-secondary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
              placeholder="ZIP"
            />
          </div>
        </div>

        {/* Country */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Country
          </label>
          <select 
            value={orgData.country}
            onChange={(e) => setOrgData(prev => ({ ...prev, country: e.target.value }))}
            className="w-full px-3 py-2 bg-background-secondary border border-accent-lavender/30 rounded text-text-primary focus:outline-none focus:border-accent-primary-base"
          >
            <option value="United States">United States</option>
            <option value="Canada">Canada</option>
            <option value="United Kingdom">United Kingdom</option>
            <option value="Australia">Australia</option>
            <option value="China">China</option>
            <option value="Germany">Germany</option>
            <option value="France">France</option>
            <option value="India">India</option>
            <option value="Japan">Japan</option>
            <option value="Singapore">Singapore</option>
            <option value="Netherlands">Netherlands</option>
            <option value="Switzerland">Switzerland</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      {/* Save Button */}
      <div className="pt-4">
        <button 
          onClick={saveOrganizationData}
          disabled={saving}
          className="bg-accent-primary-base text-text-inverse px-3 py-1 rounded-full text-xs font-medium hover:bg-accent-primary-vibrant transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

// API Keys Tab Content Component  
const ApiKeysContent = ({ adminService }) => {
  console.log('🔍 SETTINGS: Getting session data from Stytch...');
  const { isAuthenticated, session } = useStytchAuth();
  const [clients, setClients] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [newClientSecret, setNewClientSecret] = React.useState(null);

  // Load M2M clients only when authenticated and have adminService
  React.useEffect(() => {
    if (isAuthenticated() && session && adminService) {
      loadClients();
    } else {
      setLoading(false);
      if (!isAuthenticated() || !session) {
        setError('Authentication required to view API keys');
      }
    }
  }, [session, adminService]);

  const loadClients = async () => {
    if (!adminService) {
      setError('Admin service not available');
      setLoading(false);
      return;
    }

    try {
      console.log("************************** Loading clients")
      setLoading(true);
      setError(null);
      
      const data = await adminService.listClients();
      
      // Format clients for display
      const formattedClients = data.clients.map(client => ({
        ...adminService.formatClientForDisplay(client)
      }));
      
      setClients(formattedClients);
    } catch (err) {
      console.error('Failed to load M2M clients:', err);
      setError(err.message);
      // Fall back to empty state
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    if (!adminService) {
      setError('Admin service not available');
      return;
    }

    try {
      await adminService.deleteClient(clientId);
      
      // Reload the clients list
      await loadClients();
    } catch (err) {
      console.error('Failed to delete client:', err);
      setError(err.message);
    }
  };

  const handleCreateClient = async (clientData) => {
    if (!adminService) {
      throw new Error('Admin service not available');
    }

    try {
      const result = await adminService.createClient(clientData);
      
      // Store the client secret to show it to the user (only chance!)
      setNewClientSecret(result);
      
      // Reload the clients list
      await loadClients();
      
      return result;
    } catch (err) {
      console.error('Failed to create client:', err);
      throw err;
    }
  };

  return (
  <div className="space-y-6">
    {/* Header with Create Button */}
    <div className="flex justify-between items-center">
      <div>
        <h3 className="text-lg font-medium text-text-primary">API Keys</h3>
        <p className="text-text-secondary text-sm">Manage your M2M clients for accessing Lumoz services</p>
      </div>
      <button 
        onClick={() => setShowCreateModal(true)}
        className="bg-accent-primary-base text-text-inverse px-3 py-1 rounded-full text-xs font-medium hover:bg-accent-primary-vibrant transition-colors"
      >
        Create New Key
      </button>
    </div>

    {/* Error Display */}
    {error && (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
        <div className="flex items-center">
          <div className="text-red-400">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h4 className="text-red-400 font-medium">Failed to load API keys</h4>
            <p className="text-red-300 text-sm mt-1">{typeof error === 'string' ? error : error.message || 'Unknown error'}</p>
            <button 
              onClick={loadClients}
              className="text-red-400 text-sm underline hover:text-red-300 mt-2"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    )}

    {/* API Keys Table */}
    <div className="bg-background-secondary border border-accent-lavender/30 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-background-tertiary">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              Name
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              Description
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              Client ID
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              Secret
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-accent-lavender/20">
          {loading ? (
            /* Loading State */
            <tr>
              <td colSpan="5" className="px-4 py-8 text-center text-text-muted">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 border-2 border-accent-primary-base border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p>Loading API keys...</p>
                </div>
              </td>
            </tr>
          ) : clients.length > 0 ? (
            /* Client Rows */
            clients.map(client => (
              <tr key={client.client_id}>
                <td className="px-4 py-3 text-sm text-text-primary">
                  {client.displayName}
                </td>
                <td className="px-4 py-3 text-sm text-text-secondary">
                  {client.client_description || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-text-secondary font-mono">
                  <span className="flex items-center gap-1.5">
                    {'•••' + (client.client_id?.slice(-4) || '')}
                    <button
                      onClick={() => navigator.clipboard.writeText(client.client_id)}
                      className="text-text-muted hover:text-text-primary transition-colors"
                      title="Copy Client ID"
                    >
                      <Copy size={13} />
                    </button>
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-text-secondary font-mono">
                  {'•••' + (client.client_secret_last_four || '????')}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    className="text-text-muted hover:text-status-danger p-1.5 rounded transition-colors"
                    onClick={() => handleDeleteClient(client.client_id)}
                    title="Delete API Key"
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))
          ) : (
            /* Empty State */
            <tr>
              <td colSpan="5" className="px-4 py-8 text-center text-text-muted">
                <div className="flex flex-col items-center">
                  <Key size={48} className="mb-4 opacity-50" />
                  <p>No API keys found</p>
                  <p className="text-sm">Create your first API key to get started</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>

    {/* Create M2M Client Modal */}
    {showCreateModal && (
      <CreateClientModal
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateClient}
      />
    )}

    {/* Client Secret Display Modal */}
    {newClientSecret && (
      <ClientSecretModal
        clientData={newClientSecret}
        onClose={() => setNewClientSecret(null)}
      />
    )}
  </div>
  );
};

// Modal for creating new M2M clients
const CreateClientModal = ({ onClose, onCreate }) => {
  const [clientName, setClientName] = React.useState('');
  const [clientDescription, setClientDescription] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError(null);

    try {
      await onCreate({
        client_name: clientName,
        client_description: clientDescription
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background-secondary border border-accent-lavender/30 rounded-lg p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-bold text-text-primary mb-4">Create New API Key</h3>
        
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded p-3 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Client Name *
            </label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className="w-full px-3 py-2 bg-background-primary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
              placeholder="My Production API Client"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Description
            </label>
            <textarea
              value={clientDescription}
              onChange={(e) => setClientDescription(e.target.value)}
              className="w-full px-3 py-2 bg-background-primary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
              placeholder="Used for production telemetry collection"
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-accent-lavender/30 rounded text-text-secondary hover:text-text-primary hover:border-accent-lavender/50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !clientName}
              className="flex-1 bg-accent-primary-base text-text-inverse px-4 py-2 rounded font-medium hover:bg-accent-primary-vibrant transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? 'Creating...' : 'Create Key'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal for displaying the newly created client secret
const ClientSecretModal = ({ clientData, onClose }) => {
  const [copied, setCopied] = React.useState(false);

  const lumozKey = `${clientData.client_id}:${clientData.client_secret}`;

  const copyLumozKey = () => {
    navigator.clipboard.writeText(lumozKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background-secondary border border-accent-lavender/30 rounded-lg p-6 w-full max-w-lg mx-4">
        <h3 className="text-lg font-bold text-text-primary mb-4">API Key Created Successfully!</h3>

        <div className="bg-accent-amber/10 border border-accent-amber/30 rounded p-4 mb-4">
          <p className="text-accent-amber text-sm font-medium mb-2">⚠️ Save the Lumoz API key.</p>
          <p className="text-text-secondary text-sm">
            This is the only time the client secret will be shown. Make sure to copy and store it securely.
          </p>
        </div>

        <div className="space-y-3 mb-6">
          <div>
            <label className="block text-sm font-medium text-text-muted mb-1">Client ID</label>
            <code className="block bg-background-primary border border-accent-lavender/30 rounded px-3 py-2 text-text-primary font-mono text-sm">
              {clientData.client_id}
            </code>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-muted mb-1">Client Secret</label>
            <code className="block bg-background-primary border border-accent-lavender/30 rounded px-3 py-2 text-text-primary font-mono text-sm break-all">
              {clientData.client_secret}
            </code>
          </div>
        </div>

        <div className="bg-background-tertiary border border-accent-lavender/20 rounded p-3 mb-4">
          <code className="block text-xs text-text-primary font-mono">
            LUMOZ_API_KEY={lumozKey}
          </code>
        </div>

        <div className="flex gap-3">
          <button
            onClick={copyLumozKey}
            className="flex-1 bg-accent-primary-base text-text-inverse px-4 py-2 rounded font-medium hover:bg-accent-primary-vibrant transition-colors"
          >
            {copied ? '✓ Copied!' : 'Copy Lumoz Key'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-accent-primary-base text-text-inverse px-4 py-2 rounded font-medium hover:bg-accent-primary-vibrant transition-colors"
          >
            I've Saved the Secret
          </button>
        </div>
      </div>
    </div>
  );
};

// LLM Provider API Keys Content Component (for Playground)
const PROVIDERS = [
  { id: 'openai', name: 'OpenAI', description: 'GPT-4o, GPT-4, GPT-3.5 models' },
  { id: 'anthropic', name: 'Anthropic', description: 'Claude 3.5, Claude 3 models' },
  { id: 'google', name: 'Google', description: 'Gemini 2.0, Gemini 1.5 models' },
];

const ProviderKeysContent = () => {
  const { stytch } = useStytchAuth();
  const [providerKeys, setProviderKeys] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [selectedProvider, setSelectedProvider] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  // Load provider keys on mount
  React.useEffect(() => {
    loadProviderKeys();
  }, [stytch]);

  const loadProviderKeys = async () => {
    if (!stytch) return;

    try {
      setLoading(true);
      setError(null);

      const jwt = stytch?.session.getTokens()?.session_jwt;
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${apiUrl}/data/settings/api-keys`, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load provider keys: ${response.status}`);
      }

      const data = await response.json();
      setProviderKeys(data.api_keys || []);
    } catch (err) {
      console.error('Failed to load provider keys:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKey = async (provider, apiKey, keyName) => {
    if (!stytch) return;

    try {
      setSaving(true);
      setError(null);

      const jwt = stytch?.session.getTokens()?.session_jwt;
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${apiUrl}/data/settings/api-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          provider,
          api_key: apiKey,
          key_name: keyName
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to save key: ${response.status}`);
      }

      // Reload keys after save
      await loadProviderKeys();
      setShowAddModal(false);
      setSelectedProvider(null);
    } catch (err) {
      console.error('Failed to save provider key:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKey = async (provider) => {
    if (!window.confirm(`Are you sure you want to delete the ${provider} API key?`)) {
      return;
    }

    if (!stytch) return;

    try {
      setError(null);

      const jwt = stytch?.session.getTokens()?.session_jwt;
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${apiUrl}/data/settings/api-keys/${provider}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to delete key: ${response.status}`);
      }

      // Reload keys after delete
      await loadProviderKeys();
    } catch (err) {
      console.error('Failed to delete provider key:', err);
      setError(err.message);
    }
  };

  // Check if a provider has a configured key
  const getProviderKey = (providerId) => {
    return providerKeys.find(k => k.provider === providerId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-text-primary">LLM Provider API Keys</h3>
        <p className="text-text-secondary text-sm">Configure API keys for LLM providers to use in the Playground</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-400 text-sm">{error}</p>
          <button
            onClick={loadProviderKeys}
            className="text-red-400 text-sm underline hover:text-red-300 mt-2"
          >
            Try again
          </button>
        </div>
      )}

      {/* Not required notice */}
      <div className="p-3 bg-blue-500/20 border border-blue-500/40 rounded text-sm text-text-secondary">
        <strong className="text-text-primary">Not required for observability</strong> – your traces work without these keys.
      </div>

      {/* Provider Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading ? (
          <div className="col-span-2 flex items-center justify-center py-8">
            <div className="w-8 h-8 border-2 border-accent-primary-base border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-text-secondary">Loading provider keys...</span>
          </div>
        ) : (
          PROVIDERS.map(provider => {
            const configuredKey = getProviderKey(provider.id);
            const isConfigured = !!configuredKey;

            return (
              <div
                key={provider.id}
                className="bg-background-secondary border border-accent-lavender/30 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-accent-primary-base/10">
                      <Sparkles size={20} className="text-accent-primary-base" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-text-primary">{provider.name}</h4>
                      <p className="text-xs text-text-muted">{provider.description}</p>
                    </div>
                  </div>
                  <div
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      isConfigured
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-gray-500/20 text-text-muted'
                    }`}
                  >
                    {isConfigured ? 'Configured' : 'Not configured'}
                  </div>
                </div>

                {isConfigured && (
                  <div className="mb-3 px-3 py-2 bg-background-tertiary rounded text-xs">
                    <span className="text-text-muted">Key: </span>
                    <span className="text-text-secondary font-mono">
                      {configuredKey.key_hint || '••••••••'}
                    </span>
                    {configuredKey.key_name && (
                      <>
                        <span className="text-text-muted ml-2">({configuredKey.key_name})</span>
                      </>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedProvider(provider);
                      setShowAddModal(true);
                    }}
                    className="flex-1 px-3 py-1.5 bg-accent-primary-base text-text-inverse rounded text-xs font-medium hover:bg-accent-primary-vibrant transition-colors"
                  >
                    {isConfigured ? 'Update Key' : 'Add Key'}
                  </button>
                  {isConfigured && (
                    <button
                      onClick={() => handleDeleteKey(provider.id)}
                      className="px-3 py-1.5 border border-red-400/30 text-red-400 rounded text-xs font-medium hover:bg-red-400/10 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Info Box */}
      <div className="bg-background-tertiary border border-accent-lavender/20 rounded-lg p-4">
        <h4 className="text-sm font-medium text-text-primary mb-2">About Provider API Keys</h4>
        <ul className="text-xs text-text-secondary space-y-1">
          <li>• <strong className="text-text-primary">Not required for observability</strong> – your traces work without these keys.</li>
          <li>• API keys are encrypted before storage and never displayed after entry</li>
          <li>• Keys are used by the Playground to make LLM API calls on your behalf</li>
          <li>• You can get API keys from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-accent-amber hover:underline">OpenAI</a>, <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-accent-amber hover:underline">Anthropic</a>, or <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="text-accent-amber hover:underline">Google AI Studio</a></li>
          <li>• Keys are scoped to your organization and shared across all team members</li>
        </ul>
      </div>

      {/* Add/Update Key Modal */}
      {showAddModal && selectedProvider && (
        <AddProviderKeyModal
          provider={selectedProvider}
          existingKey={getProviderKey(selectedProvider.id)}
          onClose={() => {
            setShowAddModal(false);
            setSelectedProvider(null);
          }}
          onSave={handleSaveKey}
          saving={saving}
        />
      )}
    </div>
  );
};

// Modal for adding/updating provider API key
const AddProviderKeyModal = ({ provider, existingKey, onClose, onSave, saving }) => {
  const [apiKey, setApiKey] = React.useState('');
  const [keyName, setKeyName] = React.useState(existingKey?.key_name || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    onSave(provider.id, apiKey.trim(), keyName.trim());
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background-secondary border border-accent-lavender/30 rounded-lg p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-bold text-text-primary mb-4">
          {existingKey ? 'Update' : 'Add'} {provider.name} API Key
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              API Key *
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="w-full px-3 py-2 bg-background-primary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base font-mono"
              placeholder={`Enter your ${provider.name} API key`}
              required
              autoComplete="off"
            />
            <p className="text-xs text-text-muted mt-1">
              Your key will be encrypted before storage
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Key Name (optional)
            </label>
            <input
              type="text"
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              className="w-full px-3 py-2 bg-background-primary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
              placeholder="e.g., Production Key"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-accent-lavender/30 rounded text-text-secondary hover:text-text-primary hover:border-accent-lavender/50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !apiKey.trim()}
              className="flex-1 bg-accent-primary-base text-text-inverse px-4 py-2 rounded font-medium hover:bg-accent-primary-vibrant transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : existingKey ? 'Update Key' : 'Add Key'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
